import { get } from 'lodash';
import moment from 'moment';
import { forwardRef, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import StyledInput, { StyledInputProps, labelClassNames } from '~/components/ui/Form/StyledInput';

import 'react-datepicker/dist/react-datepicker.css';

const DatePickerInput = forwardRef<HTMLInputElement, StyledInputProps>((props, ref) => (
    <StyledInput ref={ref} fullWidth readOnly {...props} />
));
DatePickerInput.displayName = 'DatePickerInput';

interface FormularDateInputProps {
    name: string;
    label?: string;
    helperText?: string;
    minDaysRelative?: number;
    maxDaysRelative?: number;
    snapTo?: 'start_of_month' | 'start_of_week' | 'day_of_month';
    snapDay?: number;
}

const FormularDateInput = ({
    name,
    label,
    helperText,
    minDaysRelative,
    maxDaysRelative,
    snapTo,
    snapDay = 1
}: FormularDateInputProps) => {
    const {
        control,
        formState: { errors }
    } = useFormContext();
    const error = get(errors, name);

    const parseValue = (value: any) => {
        if (!value) return undefined;
        const date = moment(value);
        return date.isValid() ? date.hours(date.utcOffset() / 60).toDate() : undefined;
    };

    const { minDate, maxDate } = useMemo(() => {
        const today = new Date();
        return {
            minDate: minDaysRelative != null ? new Date(today.getTime() + minDaysRelative * 86_400_000) : undefined,
            maxDate: maxDaysRelative != null ? new Date(today.getTime() + maxDaysRelative * 86_400_000) : undefined
        };
    }, [minDaysRelative, maxDaysRelative]);

    const filterDate = useMemo(() => {
        if (snapTo === 'start_of_month') return (date: Date) => date.getDate() === 1;
        if (snapTo === 'day_of_month') return (date: Date) => date.getDate() === snapDay;
        if (snapTo === 'start_of_week') return (date: Date) => date.getDay() === 1;
        return undefined;
    }, [snapTo, snapDay]);

    return (
        <div className="tw-my-1 tw-w-full">
            {label && <label className={labelClassNames({ isInvalid: !!error })}>{label}</label>}
            <Controller
                control={control}
                name={name}
                render={({ field: { value, onChange, onBlur } }) => (
                    <ReactDatePicker
                        selected={parseValue(value)}
                        onChange={(date) => onChange(parseValue(date as Date))}
                        onBlur={onBlur}
                        showYearDropdown
                        dateFormat="dd.MM.yyyy"
                        placeholderText="TT.MM.JJJJ"
                        wrapperClassName="tw-w-full"
                        minDate={minDate}
                        maxDate={maxDate}
                        filterDate={filterDate}
                        customInput={
                            <DatePickerInput
                                isInvalid={!!error}
                                helperText={(error?.message as string) || helperText}
                            />
                        }
                    />
                )}
            />
        </div>
    );
};

export default FormularDateInput;
